import type { TrackingConsent } from "./event-schema"

const CLIENT_COOKIE = "alif_cid"
const SESSION_COOKIE = "alif_sid"
const FBP_COOKIE = "_fbp"
const FBC_COOKIE = "_fbc"
const FPID_COOKIE = "FPID"
const EXTERNAL_ID_COOKIE = "external_id"
const CONSENT_COOKIE = "alif_consent"
export const GUEST_IDENTITY_COOKIE = "alif_emq_identity"

const THIRTEEN_MONTHS = 60 * 60 * 24 * 395
const THIRTY_MINUTES = 60 * 30
const NINETY_DAYS = 60 * 60 * 24 * 90
const ONE_YEAR = 60 * 60 * 24 * 365
export const GUEST_IDENTITY_MAX_AGE = 60 * 60 * 24 * 60

// BD market decision — no CMP; an explicit alif_consent cookie can still
// downgrade to denied per-user.
export const DEFAULT_CONSENT: TrackingConsent = {
  analytics_storage: "granted",
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  functional_storage: "granted",
}

type CookieOptions = {
  httpOnly?: boolean
  sameSite?: "Lax" | "Strict" | "None"
  secure?: boolean
  path?: string
}

export type GuestIdentity = {
  email?: string
  phone?: string
  first_name?: string
  last_name?: string
}

export function getCookieValue(cookieHeader: string | undefined, name: string) {
  if (!cookieHeader) return undefined
  const prefix = `${name}=`
  const part = cookieHeader
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(prefix))
  return part ? decodeURIComponent(part.slice(prefix.length)) : undefined
}

export function serializeTrackingCookie(
  name: string,
  value: string,
  maxAgeSeconds: number,
  options: CookieOptions = {}
) {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSeconds}`,
    `Path=${options.path || "/"}`,
    `SameSite=${options.sameSite || "Lax"}`,
  ]
  if (options.secure !== false) parts.push("Secure")
  if (options.httpOnly) parts.push("HttpOnly")
  return parts.join("; ")
}

export function generateAlifClientId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16)
    const value = char === "x" ? rand : (rand & 0x3) | 0x8
    return value.toString(16)
  })
}

function randomDigits(length: number) {
  const digits: string[] = []
  if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
    const values = new Uint8Array(length)
    crypto.getRandomValues(values)
    values.forEach((value) => digits.push(String(value % 10)))
    return digits.join("")
  }
  for (let i = 0; i < length; i++) {
    digits.push(String(Math.floor(Math.random() * 10)))
  }
  return digits.join("")
}

// Meta `_fbp` / `_fbc` spec: fb.{subdomainIndex}.{creationTimeMs}.{value}
// creationTime MUST be Unix time in MILLISECONDS (13 digits). Emitting seconds
// makes Meta treat the value as invalid and fall back to a throwaway server-side
// fbp per request, so the agency sees "no fbp/fbc assigned".
export function generateFbp(opts?: { nowMs?: number; randomDigits?: string }) {
  const nowMs = opts?.nowMs ?? Date.now()
  return `fb.1.${nowMs}.${opts?.randomDigits || randomDigits(16)}`
}

export function buildFbcFromFbclid(fbclid: string, nowMs = Date.now()) {
  const clean = fbclid.trim()
  if (!clean) return undefined
  return `fb.1.${nowMs}.${clean}`
}

// A correctly-formed Meta cookie has a 13-digit (millisecond) timestamp in the
// 3rd segment. Anything else (e.g. the legacy 10-digit seconds format) is stale.
function metaTimestampIsMs(value: string): boolean {
  const parts = value.split(".")
  return parts.length >= 4 && /^\d{13}$/.test(parts[2])
}

// Re-mint a stale fbp (regenerate — the old random was attached to a malformed,
// unusable value anyway). Returns changed:false when already valid.
export function migrateFbp(value?: string): { value?: string; changed: boolean } {
  if (!value) return { value: undefined, changed: false }
  if (metaTimestampIsMs(value)) return { value, changed: false }
  return { value: generateFbp(), changed: true }
}

// Fix a stale fbc while PRESERVING the original fbclid (everything after the 3rd
// dot) — only the timestamp is corrected to milliseconds.
export function migrateFbc(value?: string): { value?: string; changed: boolean } {
  if (!value) return { value: undefined, changed: false }
  const parts = value.split(".")
  if (parts.length >= 4 && /^\d{13}$/.test(parts[2])) return { value, changed: false }
  const fbclid = parts.slice(3).join(".")
  if (!fbclid) return { value, changed: false }
  return { value: `fb.1.${Date.now()}.${fbclid}`, changed: true }
}

// Server-side fbclid recovery: pull fbclid from a landing URL / referrer when the
// client missed it (SPA navigation, stripped query param, etc.).
export function fbclidFromUrl(url?: string | null): string | undefined {
  if (!url) return undefined
  try {
    const value = new URL(url).searchParams.get("fbclid")?.trim()
    return value || undefined
  } catch {
    return undefined
  }
}

function normalizeEmail(email?: string | null) {
  const clean = email?.trim().toLowerCase()
  return clean || undefined
}

function normalizePhone(phone?: string | null) {
  const clean = phone?.trim()
  if (!clean) return undefined
  const digits = clean.replace(/[^\d]/g, "")
  if (!digits) return undefined
  if (digits.startsWith("880")) return `+${digits}`
  if (digits.startsWith("0")) return `+88${digits}`
  if (digits.startsWith("1") && digits.length === 10) return `+880${digits}`
  return `+${digits}`
}

function normalizeName(value?: string | null) {
  const clean = value?.trim().toLowerCase()
  return clean || undefined
}

export function normalizeGuestIdentity(
  userData?: {
    email?: string | null
    phone?: string | null
    first_name?: string | null
    last_name?: string | null
  } | null
): GuestIdentity | undefined {
  if (!userData) return undefined

  const identity: GuestIdentity = {
    email: normalizeEmail(userData.email),
    phone: normalizePhone(userData.phone),
    first_name: normalizeName(userData.first_name),
    last_name: normalizeName(userData.last_name),
  }

  Object.keys(identity).forEach((key) => {
    if (!identity[key as keyof GuestIdentity]) {
      delete identity[key as keyof GuestIdentity]
    }
  })

  return Object.keys(identity).length ? identity : undefined
}

export function hasGuestIdentityMatchData(
  identity?: GuestIdentity | null
): identity is GuestIdentity {
  return Boolean(identity?.email || identity?.phone)
}

export function encodeGuestIdentityCookie(identity: GuestIdentity) {
  return Buffer.from(JSON.stringify({ v: 1, ...identity }), "utf8").toString(
    "base64url"
  )
}

export function decodeGuestIdentityCookie(value: string | undefined) {
  if (!value) return undefined

  try {
    const parsed = JSON.parse(
      Buffer.from(value, "base64url").toString("utf8")
    ) as Partial<GuestIdentity> & { v?: number }
    return normalizeGuestIdentity(parsed)
  } catch {
    return undefined
  }
}

/**
 * Carries the shopper's user-agent + client IP from /api/events (a Route Handler,
 * where `headers()` works) to placeOrder's Server Action (where `await headers()`
 * throws "reading 'bind'" in the production build). Without this bridge the
 * backend Meta CAPI Purchase ships with no client_user_agent / client_ip_address.
 * httpOnly and first-party — never read by the browser.
 */
export const MATCH_CONTEXT_COOKIE = "alif_mctx"

export type MatchContext = { ua?: string; ip?: string }

export function encodeMatchContextCookie(context: MatchContext) {
  return Buffer.from(JSON.stringify({ v: 1, ...context }), "utf8").toString(
    "base64url"
  )
}

export function decodeMatchContextCookie(
  value: string | undefined
): MatchContext | undefined {
  if (!value) return undefined

  try {
    const parsed = JSON.parse(
      Buffer.from(value, "base64url").toString("utf8")
    ) as MatchContext & { v?: number }
    const ua = typeof parsed.ua === "string" ? parsed.ua.trim() : ""
    const ip = typeof parsed.ip === "string" ? parsed.ip.trim() : ""
    if (!ua && !ip) return undefined
    return { ...(ua ? { ua } : {}), ...(ip ? { ip } : {}) }
  } catch {
    return undefined
  }
}

function canPersistAdCookies(consent?: TrackingConsent) {
  return consent?.ad_storage === "granted"
}

function canPersistFunctionalCookies(consent?: TrackingConsent) {
  return !consent || consent.functional_storage !== "denied"
}

function setBrowserCookie(name: string, value: string, maxAge: number) {
  if (typeof document === "undefined") return
  document.cookie = serializeTrackingCookie(name, value, maxAge)
}

function readBrowserCookie(name: string) {
  if (typeof document === "undefined") return undefined
  return getCookieValue(document.cookie, name)
}

function isConsentState(value: unknown): value is "granted" | "denied" {
  return value === "granted" || value === "denied"
}

export function parseConsentCookie(value: string | undefined): TrackingConsent {
  if (!value) return DEFAULT_CONSENT

  try {
    const parsed = JSON.parse(value) as Partial<TrackingConsent>
    return {
      analytics_storage: isConsentState(parsed.analytics_storage)
        ? parsed.analytics_storage
        : DEFAULT_CONSENT.analytics_storage,
      ad_storage: isConsentState(parsed.ad_storage)
        ? parsed.ad_storage
        : DEFAULT_CONSENT.ad_storage,
      ad_user_data: isConsentState(parsed.ad_user_data)
        ? parsed.ad_user_data
        : DEFAULT_CONSENT.ad_user_data,
      ad_personalization: isConsentState(parsed.ad_personalization)
        ? parsed.ad_personalization
        : DEFAULT_CONSENT.ad_personalization,
      functional_storage: isConsentState(parsed.functional_storage)
        ? parsed.functional_storage
        : DEFAULT_CONSENT.functional_storage,
    }
  } catch {
    return DEFAULT_CONSENT
  }
}

export function getBrowserTrackingConsent(): TrackingConsent {
  return parseConsentCookie(readBrowserCookie(CONSENT_COOKIE))
}

export function ensureBrowserIdentity(consent?: TrackingConsent) {
  const client_id = readBrowserCookie(CLIENT_COOKIE) || generateAlifClientId()
  const session_id = readBrowserCookie(SESSION_COOKIE) || generateAlifClientId()

  if (canPersistFunctionalCookies(consent)) {
    setBrowserCookie(CLIENT_COOKIE, client_id, THIRTEEN_MONTHS)
    setBrowserCookie(SESSION_COOKIE, session_id, THIRTY_MINUTES)
  }

  let fbp = readBrowserCookie(FBP_COOKIE)
  let fbc = readBrowserCookie(FBC_COOKIE)
  let external_id = readBrowserCookie(EXTERNAL_ID_COOKIE)

  if (canPersistAdCookies(consent)) {
    if (!fbp) {
      fbp = generateFbp()
      setBrowserCookie(FBP_COOKIE, fbp, NINETY_DAYS)
    } else {
      const migrated = migrateFbp(fbp)
      if (migrated.changed && migrated.value) {
        fbp = migrated.value
        setBrowserCookie(FBP_COOKIE, fbp, NINETY_DAYS)
      }
    }

    const fbclid = new URLSearchParams(window.location.search).get("fbclid")
    if (fbclid) {
      fbc = buildFbcFromFbclid(fbclid)
      if (fbc) setBrowserCookie(FBC_COOKIE, fbc, NINETY_DAYS)
    } else if (fbc) {
      const migrated = migrateFbc(fbc)
      if (migrated.changed && migrated.value) {
        fbc = migrated.value
        setBrowserCookie(FBC_COOKIE, fbc, NINETY_DAYS)
      }
    }

    const externalIdFromUrl = new URLSearchParams(window.location.search).get(
      "external_id"
    )
    if (externalIdFromUrl?.trim()) {
      external_id = externalIdFromUrl.trim()
      setBrowserCookie(EXTERNAL_ID_COOKIE, external_id, ONE_YEAR)
    } else if (!external_id) {
      external_id = generateAlifClientId()
      setBrowserCookie(EXTERNAL_ID_COOKIE, external_id, ONE_YEAR)
    }
  }

  return {
    client_id,
    session_id,
    fbp,
    fbc,
    fpid: readBrowserCookie(FPID_COOKIE),
    gclid: canPersistAdCookies(consent)
      ? new URLSearchParams(window.location.search).get("gclid") || undefined
      : undefined,
    gbraid: canPersistAdCookies(consent)
      ? new URLSearchParams(window.location.search).get("gbraid") || undefined
      : undefined,
    wbraid: canPersistAdCookies(consent)
      ? new URLSearchParams(window.location.search).get("wbraid") || undefined
      : undefined,
    external_id,
  }
}

export function buildServerCookieHeaders(opts: {
  cookieHeader?: string
  consent: TrackingConsent
  responseCookies: Array<{ name: string; value: string; maxAge: number; httpOnly?: boolean }>
  preferredClientId?: string
  preferredSessionId?: string
  preferredFbp?: string
  preferredFbc?: string
}) {
  const incomingClientId = getCookieValue(opts.cookieHeader, CLIENT_COOKIE)
  const incomingSessionId = getCookieValue(opts.cookieHeader, SESSION_COOKIE)
  const clientId = incomingClientId || opts.preferredClientId || generateAlifClientId()
  const sessionId =
    incomingSessionId || opts.preferredSessionId || generateAlifClientId()

  if (!incomingClientId) {
    opts.responseCookies.push({
      name: CLIENT_COOKIE,
      value: clientId,
      maxAge: THIRTEEN_MONTHS,
      httpOnly: false,
    })
  }
  if (!incomingSessionId) {
    opts.responseCookies.push({
      name: SESSION_COOKIE,
      value: sessionId,
      maxAge: THIRTY_MINUTES,
      httpOnly: false,
    })
  }

  const incomingFbp = getCookieValue(opts.cookieHeader, FBP_COOKIE)
  let fbp = incomingFbp || opts.preferredFbp
  if (canPersistAdCookies(opts.consent)) {
    let setFbp = false
    if (!fbp) {
      fbp = generateFbp()
      setFbp = true
    } else {
      const migrated = migrateFbp(fbp)
      if (migrated.changed && migrated.value) {
        fbp = migrated.value
        setFbp = true
      } else if (!incomingFbp) {
        // valid value arrived in the payload but isn't a cookie yet
        setFbp = true
      }
    }
    if (fbp && setFbp) {
      opts.responseCookies.push({
        name: FBP_COOKIE,
        value: fbp,
        maxAge: NINETY_DAYS,
        httpOnly: false,
      })
    }
  }

  const incomingFbc = getCookieValue(opts.cookieHeader, FBC_COOKIE)
  let fbc = incomingFbc || opts.preferredFbc
  if (fbc && canPersistAdCookies(opts.consent)) {
    let setFbc = false
    const migrated = migrateFbc(fbc)
    if (migrated.changed && migrated.value) {
      fbc = migrated.value
      setFbc = true
    } else if (!incomingFbc) {
      setFbc = true
    }
    if (setFbc) {
      opts.responseCookies.push({
        name: FBC_COOKIE,
        value: fbc,
        maxAge: NINETY_DAYS,
        httpOnly: false,
      })
    }
  }

  const externalId = getCookieValue(opts.cookieHeader, EXTERNAL_ID_COOKIE)

  return {
    client_id: clientId,
    session_id: sessionId,
    fbp,
    fbc,
    fpid: getCookieValue(opts.cookieHeader, FPID_COOKIE),
    external_id: externalId,
  }
}
